import { useState } from 'react';
import {
  Clock,
  CheckCircle2,
  ChefHat,
  Truck,
  XCircle,
  Eye,
  Phone,
  MapPin,
  Package,
  Search,
  Filter,
} from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { mockOrders } from '@/services/mockData';

type OrderStatus = 'all' | 'pending' | 'confirmed' | 'preparing' | 'on_the_way' | 'delivered' | 'cancelled';

const statusConfig: Record<string, { icon: typeof Clock; color: string; bg: string }> = {
  pending: { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-100 dark:bg-amber-900/30' },
  confirmed: { icon: CheckCircle2, color: 'text-blue-600', bg: 'bg-blue-100 dark:bg-blue-900/30' },
  preparing: { icon: ChefHat, color: 'text-indigo-600', bg: 'bg-indigo-100 dark:bg-indigo-900/30' },
  on_the_way: { icon: Truck, color: 'text-purple-600', bg: 'bg-purple-100 dark:bg-purple-900/30' },
  delivered: { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-100 dark:bg-emerald-900/30' },
  cancelled: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100 dark:bg-red-900/30' },
};

export default function CafeOrders() {
  const { formatCurrency, formatDateTime, language } = useTranslation();
  const [activeTab, setActiveTab] = useState<OrderStatus>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<typeof mockOrders[0] | null>(null);

  // Filter orders for this cafe
  const cafeOrders = mockOrders.filter((o) => o.cafe_id === 1);

  const filteredOrders = cafeOrders.filter((order) => {
    const matchesTab = activeTab === 'all' || order.status === activeTab;
    const matchesSearch = !searchQuery ||
      order.order_number.includes(searchQuery) ||
      order.customer.includes(searchQuery) ||
      order.customer_phone.includes(searchQuery);
    return matchesTab && matchesSearch;
  });

  const tabs: { key: OrderStatus; label: Record<string, string>; count: number }[] = [
    { key: 'all', label: { ar: 'الكل', en: 'All' }, count: cafeOrders.length },
    { key: 'pending', label: { ar: 'معلق', en: 'Pending' }, count: cafeOrders.filter((o) => o.status === 'pending').length },
    { key: 'confirmed', label: { ar: 'مؤكد', en: 'Confirmed' }, count: cafeOrders.filter((o) => o.status === 'confirmed').length },
    { key: 'preparing', label: { ar: 'تحضير', en: 'Preparing' }, count: cafeOrders.filter((o) => o.status === 'preparing').length },
    { key: 'on_the_way', label: { ar: 'في الطريق', en: 'On Way' }, count: cafeOrders.filter((o) => o.status === 'on_the_way').length },
    { key: 'delivered', label: { ar: 'مكتمل', en: 'Done' }, count: cafeOrders.filter((o) => o.status === 'delivered').length },
    { key: 'cancelled', label: { ar: 'ملغي', en: 'Cancelled' }, count: cafeOrders.filter((o) => o.status === 'cancelled').length },
  ];

  const statusLabels: Record<string, Record<string, string>> = {
    pending: { ar: 'قيد الانتظار', en: 'Pending' },
    confirmed: { ar: 'مؤكد', en: 'Confirmed' },
    preparing: { ar: 'جاري التحضير', en: 'Preparing' },
    on_the_way: { ar: 'في الطريق', en: 'On the Way' },
    delivered: { ar: 'تم التوصيل', en: 'Delivered' },
    cancelled: { ar: 'ملغي', en: 'Cancelled' },
  };

  const getNextAction = (status: string): { label: Record<string, string>; color: string } | null => {
    switch (status) {
      case 'pending': return { label: { ar: 'تأكيد الطلب', en: 'Confirm Order' }, color: 'bg-blue-600 hover:bg-blue-700' };
      case 'confirmed': return { label: { ar: 'بدء التحضير', en: 'Start Preparing' }, color: 'bg-indigo-600 hover:bg-indigo-700' };
      case 'preparing': return { label: { ar: 'جاهز للتوصيل', en: 'Ready for Delivery' }, color: 'bg-purple-600 hover:bg-purple-700' };
      default: return null;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
            {language === 'ar' ? 'إدارة الطلبات' : 'Order Management'}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {language === 'ar' ? 'تتبع وإدارة طلبات المقهى' : 'Track and manage cafe orders'}
          </p>
        </div>
      </div>

      {/* Tab filters */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-all flex items-center gap-2 ${
              activeTab === tab.key
                ? 'bg-amber-600 text-white shadow-lg shadow-amber-200 dark:shadow-amber-900/30'
                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-amber-300'
            }`}
          >
            {tab.label[language]}
            <span className={`px-1.5 py-0.5 rounded-md text-xs ${
              activeTab === tab.key
                ? 'bg-white/20'
                : 'bg-slate-100 dark:bg-slate-700'
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={18} className="absolute start-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder={language === 'ar' ? 'بحث برقم الطلب أو اسم العميل...' : 'Search by order number or customer name...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full ps-11 pe-4 py-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
        />
      </div>

      {/* Orders Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filteredOrders.map((order) => {
          const config = statusConfig[order.status];
          const StatusIcon = config?.icon || Clock;
          const nextAction = getNextAction(order.status);

          return (
            <div
              key={order.id}
              className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden hover:shadow-lg transition-shadow"
            >
              {/* Order Header */}
              <div className="p-4 border-b border-slate-100 dark:border-slate-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-lg text-slate-800 dark:text-white">{order.order_number}</span>
                  <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold flex items-center gap-1 ${config?.bg} ${config?.color}`}>
                    <StatusIcon size={14} />
                    {statusLabels[order.status]?.[language] || order.status}
                  </span>
                </div>
                <p className="text-xs text-slate-400">{formatDateTime(order.created_at)}</p>
              </div>

              {/* Customer Info */}
              <div className="p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                    <span className="text-sm font-bold text-slate-600 dark:text-slate-300">
                      {order.customer[0]}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 dark:text-slate-200">{order.customer}</p>
                    <p className="text-xs text-slate-400 flex items-center gap-1">
                      <Phone size={10} /> {order.customer_phone}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2 text-xs text-slate-500 dark:text-slate-400">
                  <MapPin size={14} className="mt-0.5 shrink-0" />
                  <span className="line-clamp-1">{order.address}</span>
                </div>

                {/* Items */}
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-3 space-y-1.5">
                  <div className="flex items-center gap-1 text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
                    <Package size={12} />
                    {order.items_count} {language === 'ar' ? 'عناصر' : 'items'}
                  </div>
                  {order.items.map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-300">
                        {item.qty}x {item.name}
                      </span>
                      <span className="font-semibold text-slate-700 dark:text-slate-200">
                        {formatCurrency(item.price * item.qty)}
                      </span>
                    </div>
                  ))}
                  <div className="border-t border-slate-200 dark:border-slate-600 pt-1.5 mt-1.5 flex justify-between">
                    <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
                      {language === 'ar' ? 'الإجمالي' : 'Total'}
                    </span>
                    <span className="text-sm font-bold text-amber-600 dark:text-amber-400">
                      {formatCurrency(order.total)}
                    </span>
                  </div>
                </div>

                {order.notes && (
                  <div className="text-xs text-slate-500 dark:text-slate-400 italic bg-amber-50 dark:bg-amber-900/10 rounded-lg p-2">
                    📝 {order.notes}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="p-4 pt-0 flex gap-2">
                <button
                  onClick={() => setSelectedOrder(order)}
                  className="flex-1 px-3 py-2 rounded-xl text-sm font-semibold bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors flex items-center justify-center gap-1"
                >
                  <Eye size={14} />
                  {language === 'ar' ? 'تفاصيل' : 'Details'}
                </button>
                {nextAction && (
                  <button className={`flex-1 px-3 py-2 rounded-xl text-sm font-semibold text-white transition-colors ${nextAction.color}`}>
                    {nextAction.label[language]}
                  </button>
                )}
                {order.status === 'pending' && (
                  <button className="px-3 py-2 rounded-xl text-sm font-semibold bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors">
                    <XCircle size={16} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filteredOrders.length === 0 && (
        <div className="text-center py-16 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700">
          <Filter size={48} className="mx-auto text-slate-300 dark:text-slate-600 mb-4" />
          <p className="text-lg font-semibold text-slate-500 dark:text-slate-400">
            {language === 'ar' ? 'لا توجد طلبات' : 'No orders found'}
          </p>
          <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
            {language === 'ar' ? 'جرب تغيير الفلتر أو البحث' : 'Try changing the filter or search'}
          </p>
        </div>
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedOrder(null)}>
          <div
            className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-800 dark:text-white">
                  {language === 'ar' ? 'تفاصيل الطلب' : 'Order Details'} {selectedOrder.order_number}
                </h2>
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  <XCircle size={20} className="text-slate-400" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Customer */}
              <div>
                <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 mb-2">
                  {language === 'ar' ? 'بيانات العميل' : 'Customer Info'}
                </h3>
                <div className="space-y-1.5">
                  <p className="font-semibold text-slate-800 dark:text-slate-200">{selectedOrder.customer}</p>
                  <p className="text-sm text-slate-500 flex items-center gap-1"><Phone size={14} /> {selectedOrder.customer_phone}</p>
                  <p className="text-sm text-slate-500 flex items-center gap-1"><MapPin size={14} /> {selectedOrder.address}</p>
                </div>
              </div>

              {/* Items */}
              <div>
                <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 mb-2">
                  {language === 'ar' ? 'العناصر' : 'Items'}
                </h3>
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-2">
                  {selectedOrder.items.map((item, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="text-slate-700 dark:text-slate-300">{item.qty}x {item.name}</span>
                      <span className="font-semibold text-slate-800 dark:text-slate-200">{formatCurrency(item.price * item.qty)}</span>
                    </div>
                  ))}
                  <div className="border-t border-slate-200 dark:border-slate-600 pt-2 space-y-1">
                    <div className="flex justify-between text-sm text-slate-500">
                      <span>{language === 'ar' ? 'المبلغ' : 'Subtotal'}</span>
                      <span>{formatCurrency(selectedOrder.amount)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-slate-500">
                      <span>{language === 'ar' ? 'رسوم التوصيل' : 'Delivery Fee'}</span>
                      <span>{formatCurrency(selectedOrder.delivery_fee)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg text-amber-600 dark:text-amber-400">
                      <span>{language === 'ar' ? 'الإجمالي' : 'Total'}</span>
                      <span>{formatCurrency(selectedOrder.total)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment & Driver */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-3">
                  <p className="text-xs text-slate-400 mb-1">{language === 'ar' ? 'طريقة الدفع' : 'Payment'}</p>
                  <p className="font-semibold text-slate-700 dark:text-slate-200 capitalize">{selectedOrder.payment_method}</p>
                </div>
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-3">
                  <p className="text-xs text-slate-400 mb-1">{language === 'ar' ? 'السائق' : 'Driver'}</p>
                  <p className="font-semibold text-slate-700 dark:text-slate-200">{selectedOrder.driver}</p>
                </div>
              </div>

              {selectedOrder.notes && (
                <div className="bg-amber-50 dark:bg-amber-900/10 rounded-xl p-3">
                  <p className="text-xs text-amber-600 dark:text-amber-400 mb-1">{language === 'ar' ? 'ملاحظات' : 'Notes'}</p>
                  <p className="text-sm text-slate-700 dark:text-slate-300">{selectedOrder.notes}</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-200 dark:border-slate-700 flex gap-3">
              <button
                onClick={() => setSelectedOrder(null)}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                {language === 'ar' ? 'إغلاق' : 'Close'}
              </button>
              {getNextAction(selectedOrder.status) && (
                <button
                  className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors ${getNextAction(selectedOrder.status)!.color}`}
                >
                  {getNextAction(selectedOrder.status)!.label[language]}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
